import { MsalProvider } from '@azure/msal-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { App } from '@/App';
import { ToastProvider } from '@/components/ui/Toast';
import { initMsal, isMsalActive, pca } from '@/lib/msal';
import { initTheme } from '@/store/theme';
import '@/styles.css';

// Re-apply the persisted theme + subscribe to OS theme changes for users
// who chose 'system'. The pre-React inline script in index.html already
// stamped <html class="dark"> for the first paint; this call rebinds the
// matchMedia listener that the inline script can't.
initTheme();

// i18n initializes synchronously at import (English needs no dictionary —
// natural keys fall back to their own text), so t() is valid from the very
// first render. Other languages lazy-load on demand via applyLocale().
import '@/lib/i18n';

const queryClient = new QueryClient({
  defaultOptions: {
    // Refresh strategy (industry-standard):
    //   • staleTime 30s — within a 30s window, navigating between pages and
    //     quick tab-switches serve from cache (snappy, no refetch storm /
    //     skeleton flash). The earlier staleTime:0 caused EVERY navigation and
    //     focus to refetch, which read as "always loading".
    //   • refetchOnWindowFocus/Reconnect stay ON — this is the actual fix for
    //     "stale until hard refresh": returning to a tab (when data is stale)
    //     or reconnecting still pulls fresh data. Mutations additionally
    //     invalidate their own queries, so a user's OWN action is reflected
    //     instantly regardless of staleTime.
    // List pages also set `placeholderData: (prev) => prev` so a refetch keeps
    // the previous rows on screen instead of blanking to a skeleton.
    queries: {
      retry: false,
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
  },
});

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('#root not found');

/**
 * Mount the tree. When MSAL is configured for this env, the entire app is
 * wrapped in <MsalProvider>; otherwise we render the same tree without it.
 * `<MsalProvider>` is the cheap part — the expensive part is `initMsal()`,
 * which is awaited so handleRedirectPromise has finished before React
 * mounts and AppShell's /me query fires.
 */
async function mount() {
  if (isMsalActive()) {
    // Sequence the MSAL bootstrap BEFORE first render. This processes any
    // OAuth redirect-response in the URL and sets the active account in
    // MSAL's cache, so by the time AuthCallback / AppShell mount they
    // already see the signed-in account.
    await initMsal();
  }

  const tree = (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </ToastProvider>
      </QueryClientProvider>
    </StrictMode>
  );

  // pca is non-null iff isMsalActive() returned true at module load.
  // The `!` is safe under that invariant.
  createRoot(rootElement!).render(
    isMsalActive() ? <MsalProvider instance={pca!}>{tree}</MsalProvider> : tree,
  );
}

mount().catch((err) => {
  // If MSAL bootstrap throws, render the tree without it so the user can
  // still see an error rather than a white screen. The Login page will
  // detect the absence of `pca` and fall back to dev mode UI.
  console.error('[main] MSAL bootstrap failed; rendering without it:', err);
  createRoot(rootElement!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </ToastProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
});
